import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globals } from '../globalfunctions.js';
import mfaEnforcer from '../utils/mfaEnforcer';
import TwoFactorAuth from './TwoFactorAuth';
import { alertDialog } from '../utils/dialogs';

const MFAGuard = ({ children }) => {
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [accessCheck, setAccessCheck] = useState({ allowed: false, reason: '' });
  
  // Debug: Log when MFAGuard mounts
  console.log('MFAGuard mounted for path:', window.location.pathname);

  const handleAccessDenied = useCallback((reason) => {
    console.log('MFAGuard: handleAccessDenied called with reason:', reason);
    switch (reason) {
      case 'not_logged_in':
        console.log('MFAGuard - User not logged in, redirecting to login');
        // Clear any partial session data
        Globals.UserName = '';
        Globals.UserType = '';
        Globals.LoginTime = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userData');
        // Store the intended URL for redirect after login
        localStorage.setItem('redirectAfterLogin', window.location.pathname);
        navigate('/login');
        break;
      case '2fa_not_setup':
        console.log('MFAGuard: 2FA not set up, forcing logout');
        // Force logout and redirect to login for 2FA setup
        Globals.UserName = '';
        Globals.UserType = '';
        Globals.LoginTime = null;
        
        // Preserve redirect URL before clearing localStorage
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        localStorage.clear(); // Clear all localStorage including userData
        if (redirectUrl) {
          localStorage.setItem('redirectAfterLogin', redirectUrl);
        }
        
        alertDialog('Two-factor authentication setup is required. Please log in to complete the setup process.');
        navigate('/login');
        break;
      case '2fa_session_expired':
        console.log('MFAGuard: 2FA session expired, redirecting to login');
        // Clear all session data when 2FA session expires
        Globals.UserName = '';
        Globals.UserType = '';
        Globals.LoginTime = null;
        
        // Clear 2FA session data
        mfaEnforcer.clear2FASession();
        
        // Preserve redirect URL before clearing localStorage
        const currentRedirectUrl = localStorage.getItem('redirectAfterLogin');
        localStorage.clear(); // Clear all localStorage including userData
        if (currentRedirectUrl) {
          localStorage.setItem('redirectAfterLogin', currentRedirectUrl);
        } else {
          localStorage.setItem('redirectAfterLogin', window.location.pathname);
        }
        
        navigate('/login');
        break;
      default:
        console.warn('MFAGuard: Unknown access denial reason:', reason);
        navigate('/login');
    }
  }, [navigate]);

  const checkAccess = useCallback(() => {
    // Don't check access for login/register pages
    const currentPath = window.location.pathname;
    console.log('MFAGuard checkAccess for path:', currentPath);
    const publicPaths = ['/login', '/Login', '/register', '/Register', '/ForgotPassword', '/ResetPassword', '/'];
    
    if (publicPaths.some(path => currentPath.toLowerCase().includes(path.toLowerCase()))) {
      console.log('MFAGuard: Public path, allowing access');
      setAccessCheck({ allowed: true });
      setIsVerifying(false);
      return;
    }

    console.log('MFAGuard: Protected path, checking access...');
    const check = mfaEnforcer.canAccessApplication();
    console.log('MFAGuard: Access check result:', check);
    setAccessCheck(check);
    
    if (!check.allowed) {
      console.log('MFAGuard: Access denied, calling handleAccessDenied with reason:', check.reason);
      handleAccessDenied(check.reason);
    } else {
      // Check if user needs periodic re-authentication
      if (mfaEnforcer.requiresPeriodicReauth()) {
        setNeedsReauth(true);
      }
    }
    
    setIsVerifying(false);
  }, [navigate, handleAccessDenied]);

  useEffect(() => {
    checkAccess();
    
    // Set up periodic checks for 2FA session validity
    const intervalId = setInterval(checkAccess, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, [checkAccess]);


  const handleReauthSuccess = (method) => {
    console.log('Re-authentication successful with method:', method);
    mfaEnforcer.markSessionAs2FAVerified();
    mfaEnforcer.markElevatedAuth();
    setNeedsReauth(false);
    
    // Refresh access check
    checkAccess();
  };

  const handleReauthCancel = () => {
    // For mandatory 2FA, force logout on cancel
    console.log('Re-authentication cancelled, logging out');
    
    // Clear all session data
    Globals.UserName = '';
    Globals.UserType = '';
    Globals.LoginTime = null;
    
    // Preserve redirect URL before clearing localStorage
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    localStorage.clear(); // Clear all localStorage including userData
    if (redirectUrl) {
      localStorage.setItem('redirectAfterLogin', redirectUrl);
    }
    
    navigate('/login');
  };

  // Show loading while checking access
  if (isVerifying) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e3e3e3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#666' }}>Verifying security credentials...</p>
        </div>
      </div>
    );
  }

  // Show re-authentication modal if needed
  if (needsReauth) {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: 'rgba(0,0,0,0.9)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '40px', 
          borderRadius: '12px', 
          maxWidth: '500px', 
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ marginBottom: '15px', color: '#dc3545' }}>Security Re-Authentication Required</h2>
          <p style={{ marginBottom: '20px', color: '#666', lineHeight: '1.5' }}>
            For your security, please verify your identity again. Your 2FA session has expired or 
            you're attempting to access sensitive features that require elevated authentication.
          </p>
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '6px', 
            marginBottom: '25px',
            fontSize: '14px',
            color: '#856404'
          }}>
            <strong>Security Policy:</strong> Re-authentication is required every 12 hours or when accessing sensitive features.
          </div>
          <TwoFactorAuth 
            mode="login"
            onSuccess={handleReauthSuccess}
            onCancel={handleReauthCancel}
          />
        </div>
      </div>
    );
  }

  // Show access denied screen for other cases
  if (!accessCheck.allowed) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '40px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚫</div>
          <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>Access Denied</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            You don't have the required security credentials to access this application.
          </p>
          <p style={{ fontSize: '14px', color: '#888', marginBottom: '25px' }}>
            Reason: {accessCheck.reason.replace(/_/g, ' ')}
          </p>
          <button 
            onClick={() => {
              // Store the intended URL for redirect after login
              localStorage.setItem('redirectAfterLogin', window.location.pathname);
              navigate('/login');
            }}
            style={{ 
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // If all checks pass, render the protected content
  return children;
};

export default MFAGuard;